import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import data from "@/data/cocktails.json";

const COCKTAILS = data.cocktails;

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.35 },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};
// Hoisted transition variant — used by the sub-headline so it lags the hero
// heading by a quarter beat. Module-scope = stable identity across renders.
const FADE_UP_DELAYED = { ...fadeUp.transition, delay: 0.15 };

function imgSrc(id) {
  return `/noor/cocktails/${id}.webp`;
}

function CocktailHero() {
  return (
    <header
      data-testid="cocktails-hero"
      className="max-w-[1480px] mx-auto px-6 md:px-10 pt-44 pb-20 md:pb-28"
    >
      <Link
        data-testid="cocktails-back"
        to="/"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase mb-12 transition-colors"
        style={{ color: "var(--text-secondary)", letterSpacing: "0.32em" }}
      >
        <ArrowLeft size={12} />
        Return to the House
      </Link>
      <p className="overline mb-5" style={{ color: "var(--accent-rose)" }}>
        VI · The Bar
      </p>
      <motion.h1
        {...fadeUp}
        className="font-display leading-[1.02] max-w-[18ch]"
        style={{ color: "var(--noor-bone)", fontSize: "clamp(2.8rem, 5.6vw, 6.4rem)", letterSpacing: "-0.025em" }}
      >
        Four ways to <em className="italic" style={{ color: "var(--accent-rose)" }}>drink</em> a small batch.
      </motion.h1>
      <motion.p
        {...fadeUp}
        transition={FADE_UP_DELAYED}
        className="font-display-light mt-10 max-w-[58ch] text-lg md:text-xl leading-[1.65]"
        style={{ color: "var(--text-secondary)" }}
      >
        These are the four cocktails the House serves at every private tasting. Built around restraint —
        the blanco is never disguised, only framed. Each recipe yields one drink. The fewer
        you make, the better they read.
      </motion.p>
    </header>
  );
}

function IngredientList({ items, id }) {
  return (
    <ul data-testid={`cocktail-${id}-ingredients`} className="space-y-2.5">
      {items.map((i) => (
        <li
          key={i}
          className="font-mono text-[11px] leading-relaxed pl-4 relative"
          style={{ color: "var(--text-secondary)", letterSpacing: "0.04em" }}
        >
          <span
            className="absolute left-0 top-2 w-1.5 h-px"
            style={{ background: "var(--accent-rose)" }}
            aria-hidden
          />
          {i}
        </li>
      ))}
    </ul>
  );
}

// Hoisted style objects — referential stability avoids re-render churn.
const CARD_IMG_STYLE = {
  aspectRatio: "4 / 5",
  background:
    "radial-gradient(ellipse 60% 50% at 50% 60%, rgba(201,164,122,0.10), transparent 70%), #0F0F13",
  border: "1px solid var(--border-subtle)",
};
const NUMERAL_STYLE = { color: "var(--accent-rose)", fontSize: "clamp(3rem, 6vw, 5.4rem)" };
const FALLBACK_LABEL_STYLE = { color: "var(--text-secondary)", letterSpacing: "0.32em" };
const ESSENCE_STYLE = { color: "var(--accent-rose)", letterSpacing: "0.32em", textTransform: "uppercase" };
const NAME_STYLE = { color: "var(--noor-bone)", fontSize: "clamp(2.4rem, 4.4vw, 3.8rem)", letterSpacing: "-0.02em" };
const STORY_STYLE = { color: "var(--text-secondary)" };
const SECTION_LABEL_STYLE = { color: "var(--text-secondary)", letterSpacing: "0.32em", textTransform: "uppercase" };
const METHOD_STYLE = { color: "var(--noor-bone)" };
const META_STYLE = { color: "var(--text-secondary)", letterSpacing: "0.28em", textTransform: "uppercase" };

function CocktailImage({ cocktail }) {
  const [errored, setErrored] = useState(false);
  return (
    <div className="relative w-full overflow-hidden rounded-sm" style={CARD_IMG_STYLE}>
      {!errored ? (
        <img
          src={imgSrc(cocktail.id)}
          alt={`${cocktail.name} — ${cocktail.essence}`}
          loading="lazy"
          decoding="async"
          onError={() => setErrored(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        // Editorial fallback if the cocktail image hasn't been generated yet.
        <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
          <p className="font-display italic mb-3" style={NUMERAL_STYLE}>
            {cocktail.numeral}
          </p>
          <p className="font-mono text-[10px] uppercase" style={FALLBACK_LABEL_STYLE}>
            Photography pending
          </p>
        </div>
      )}
    </div>
  );
}

function CocktailInfo({ cocktail }) {
  return (
    <>
      <p className="font-mono text-[10px] mb-4" style={ESSENCE_STYLE}>
        № {cocktail.numeral} · {cocktail.essence}
      </p>
      <h2 className="font-display leading-[1.05] mb-6" style={NAME_STYLE}>
        {cocktail.name}
      </h2>
      <p className="font-display-light max-w-[52ch] text-base md:text-lg leading-[1.65] mb-10" style={STORY_STYLE}>
        {cocktail.story}
      </p>
    </>
  );
}

function CocktailRecipe({ cocktail }) {
  return (
    <div className="grid grid-cols-12 gap-y-8 md:gap-8">
      <div className="col-span-12 md:col-span-5">
        <p className="font-mono text-[10px] mb-4" style={SECTION_LABEL_STYLE}>Ingredients</p>
        <IngredientList items={cocktail.ingredients} id={cocktail.id} />
      </div>
      <div className="col-span-12 md:col-span-7">
        <p className="font-mono text-[10px] mb-4" style={SECTION_LABEL_STYLE}>Method</p>
        <p
          data-testid={`cocktail-${cocktail.id}-method`}
          className="text-sm leading-[1.7]"
          style={METHOD_STYLE}
        >
          {cocktail.method}
        </p>
        <p className="font-mono text-[10px] mt-6" style={META_STYLE}>
          {cocktail.glass} · {cocktail.yield}
        </p>
      </div>
    </div>
  );
}

function CocktailCard({ cocktail, reversed }) {
  return (
    <article
      data-testid={`cocktail-${cocktail.id}`}
      className="grid grid-cols-12 gap-y-10 md:gap-12 items-center"
    >
      <div className={`col-span-12 lg:col-span-5 ${reversed ? "lg:order-2" : ""}`}>
        <CocktailImage cocktail={cocktail} />
      </div>
      <div className={`col-span-12 lg:col-span-7 ${reversed ? "lg:order-1 lg:pr-10" : "lg:pl-10"}`}>
        <CocktailInfo cocktail={cocktail} />
        <CocktailRecipe cocktail={cocktail} />
      </div>
    </article>
  );
}

function CocktailList() {
  return (
    <section className="max-w-[1480px] mx-auto px-6 md:px-10 pb-32 space-y-28 md:space-y-36">
      {COCKTAILS.map((c, i) => (
        <CocktailCard key={c.id} cocktail={c} reversed={i % 2 === 1} />
      ))}
    </section>
  );
}

function CocktailFoot() {
  return (
    <section className="max-w-[1480px] mx-auto px-6 md:px-10 pb-32 pt-12">
      <div
        className="liquid-glass p-10 md:p-14 text-center"
        data-testid="cocktails-foot"
      >
        <p className="overline mb-4" style={{ color: "var(--accent-rose)" }}>One last note</p>
        <p
          className="font-display italic mx-auto max-w-[60ch] mb-10"
          style={{ color: "var(--noor-bone)", fontSize: "clamp(1.4rem, 2.4vw, 2rem)", lineHeight: 1.4 }}
        >
          NOOR is not a mixer. If the bottle is open, drink it slowly — neat first, then with one ingredient,
          never with three.
        </p>
        <Link
          data-testid="cocktails-cta-inner-circle"
          to="/inner-circle"
          className="btn-solid"
        >
          Join the Inner Circle
        </Link>
      </div>
    </section>
  );
}

export default function Cocktails() {
  return (
    <main data-testid="cocktails-page">
      <CocktailHero />
      <CocktailList />
      <CocktailFoot />
    </main>
  );
}
